import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MapPin, Phone, Calendar, FolderOpen } from 'lucide-react'
import api from '../../services/api'

/**
 * Mobile Projects Page
 * Shows active installations optimized for touch
 */
function MobileProjectsPage() {
  const { t } = useTranslation()

  const { data: projects, isLoading } = useQuery({
    queryKey: ['mobile-projects'],
    queryFn: async () => {
      const response = await api.get('mobile/projects')
      return response.data
    }
  })

  const handleCall = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`
  }

  const handleNavigate = (address) => {
    const encodedAddress = encodeURIComponent(address)
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-canvas dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-canvas dark:bg-gray-900 p-4 space-y-4">
      <h1 className="text-2xl font-bold text-ink dark:text-white">
        {t('mobile.projects.title', { defaultValue: 'Actieve Projecten' })}
      </h1>

      {projects && projects.length > 0 ? (
        <div className="space-y-3">
          {projects.map((project) => (
            <Link
              key={project.id}
              to={`/mobile/projects/${project.id}`}
              className="block r11-card p-4"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-medium text-ink dark:text-white">
                    {project.name}
                  </h3>
                  <p className="text-sm text-muted mt-1">
                    {project.client_name}
                  </p>
                </div>
                <span className={`badge ${
                  project.status === 'in_progress' ? 'badge-warning' :
                  project.status === 'completed' ? 'badge-success' :
                  'badge-active'
                }`}>
                  {project.status}
                </span>
              </div>

              {project.address && (
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-faint" />
                  <span className="text-sm text-muted">
                    {project.address}
                  </span>
                </div>
              )}

              {project.installation_date && (
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-faint" />
                  <span className="text-sm text-muted">
                    {new Date(project.installation_date).toLocaleDateString('nl-NL')}
                  </span>
                </div>
              )}

              <div className="flex gap-2 mt-3">
                {project.client_phone && (
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      handleCall(project.client_phone)
                    }}
                    className="btn-primary flex-1 py-3"
                  >
                    <Phone className="w-4 h-4" />
                    {t('mobile.actions.call', { defaultValue: 'Bellen' })}
                  </button>
                )}
                {project.address && (
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      handleNavigate(project.address)
                    }}
                    className="btn-ghost flex-1 py-3"
                  >
                    <MapPin className="w-4 h-4" />
                    {t('mobile.actions.navigate', { defaultValue: 'Navigeer' })}
                  </button>
                )}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="r11-card p-8">
          <div className="r11-empty">
            <FolderOpen className="r11-empty__icon" />
            <p className="r11-empty__title">
              {t('mobile.projects.no_projects', { defaultValue: 'Geen actieve projecten' })}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default MobileProjectsPage
